import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Searchbar } from '@atlassian/learning-components';
import { debounce, omit } from 'lodash';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';

import { sendUIEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import {
  searchAdobeLearningObjects,
  AdobeSearchResponse,
  getDefaultAdobeLabelToValueMap,
  ADOBE_FILTER_MAPPING,
} from 'src/services/http/adobe';
import { getFiltersFromUrl } from 'src/pages/CatalogPage/loader';

interface SearchbarProps {
  onSearchInteraction: () => void;
  isLoadingResources: boolean;
  isAdobeCatalog?: boolean;
  onAdobeSearchResults?: (results: AdobeSearchResponse | null) => void;
  onAdobeSearchStart?: () => void;
}
export const SearchbarWrapper: FC<SearchbarProps> = ({
  onSearchInteraction,
  isLoadingResources,
  isAdobeCatalog = false,
  onAdobeSearchResults,
  onAdobeSearchStart,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [value, setValue] = useState('');
  const { user } = useAtlassianUser();
  const validSearchCharacters = /^[a-zA-Z0-9 ,!?"':.-]+$/;

  useEffect(() => {
    const searchValue = searchParams.get('search') || '';
    const sanitizedValue = searchValue.replaceAll(/[&\\#+()$~%*<>{}]/g, '');

    if (sanitizedValue) {
      setValue(sanitizedValue);
      updateSearchParams(sanitizedValue, true);

      if (isAdobeCatalog) {
        handleAdobeSearch(sanitizedValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchParams.get('search')) {
      setValue('');
    }
  }, [searchParams]);

  const updateSearchParams = useCallback(
    (value: string, replace: boolean = false) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (!value.length) {
        newSearchParams.delete('search');
      } else {
        newSearchParams.set('search', value);
      }

      setSearchParams(newSearchParams, { replace });
    },
    [searchParams, setSearchParams],
  );

  const debouncedRequest = useMemo(() => debounce(updateSearchParams, 1000), [updateSearchParams]);

  const handleAdobeSearch = useCallback(
    async (searchQuery: string) => {
      if (!isAdobeCatalog) {
        return;
      }

      if (!searchQuery.trim()) {
        if (onAdobeSearchResults) {
          onAdobeSearchResults(null);
        }

        return;
      }

      if (onAdobeSearchStart) {
        onAdobeSearchStart();
      }

      try {
        const currentUrl = window.location.href;
        const urlFilters = getFiltersFromUrl(currentUrl);

        const catalogIds = urlFilters.catalog || [];

        // Remove search and catalog from filters to determine if other filters exist
        const otherFilters = omit(urlFilters, ['search', 'catalog']);

        // Convert filter labels to API values for Adobe search
        let convertedFilters: { [key: string]: string[] } | undefined = undefined;

        if (Object.keys(otherFilters).length > 0) {
          const labelToValueMap = getDefaultAdobeLabelToValueMap();
          const adobeFilters: { [key: string]: string[] } = {};

          Object.keys(otherFilters).forEach((key) => {
            const adobeKey = ADOBE_FILTER_MAPPING[key as keyof typeof ADOBE_FILTER_MAPPING] || key;
            const filterLabels = otherFilters[key];

            if (labelToValueMap[key]) {
              const apiValues = filterLabels
                .map((label) => {
                  const decodedLabel = decodeURIComponent(label.replace(/\+/g, ' ')).trim();
                  const converted = labelToValueMap[key][decodedLabel] || decodedLabel;

                  return converted;
                })
                .filter((value) => value);

              if (apiValues.length > 0) {
                adobeFilters[adobeKey] = apiValues;
              }
            } else {
              adobeFilters[adobeKey] = filterLabels;
            }
          });

          // Filter out undefined values
          const cleanedFilters: { [key: string]: string[] } = {};

          Object.keys(adobeFilters).forEach((key) => {
            if (adobeFilters[key] && adobeFilters[key].length > 0) {
              cleanedFilters[key] = adobeFilters[key];
            }
          });

          convertedFilters = Object.keys(cleanedFilters).length > 0 ? cleanedFilters : undefined;
        }

        const searchResults = await searchAdobeLearningObjects(
          searchQuery,
          catalogIds.length > 0 ? catalogIds : undefined,
          convertedFilters,
        );

        if (onAdobeSearchResults) {
          onAdobeSearchResults(searchResults ?? null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Adobe search failed:', error);
      }
    },
    [isAdobeCatalog, onAdobeSearchResults, onAdobeSearchStart],
  );

  const debouncedAdobeSearch = useMemo(() => debounce(handleAdobeSearch, 500), [handleAdobeSearch]);

  // Watch for URL parameter changes and automatically trigger Adobe search
  useEffect(() => {
    if (isAdobeCatalog) {
      const currentSearchValue = searchParams.get('search') || '';

      if (currentSearchValue.trim()) {
        handleAdobeSearch(currentSearchValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdobeCatalog]);

  const onChange = (value: string): void => {
    const isEmptySearch = !value.trim().length;

    if (!validSearchCharacters.test(value) && !isEmptySearch) {
      return;
    }

    if (isEmptySearch) {
      setValue('');

      if (isAdobeCatalog) {
        debouncedAdobeSearch('');
      }
    } else {
      setValue(value);

      sendUIEvent(
        'catalogScreen',
        'search',
        'clicked',
        'catalogSearch',
        {
          value,
        },
        user?.account_id,
      );

      if (isAdobeCatalog) {
        debouncedAdobeSearch(value);
      }
    }

    onSearchInteraction();
    debouncedRequest(value);
  };

  return (
    <div css={searchbarWrapperStyles}>
      <Searchbar onChange={onChange} value={value} isLoading={isLoadingResources} />
    </div>
  );
};

const searchbarWrapperStyles = css({
  [media.above.md]: {
    width: '100%',
  },

  [media.below.md]: {
    width: '275px',
  },

  [media.below.xs]: {
    flex: 1,
  },
});
